import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import { ServiceHistApi, TDeleteServiceHistResult } from '../../services';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { IIncident, IIncidentSnapshot, Incident } from '../incident/Incident';
import { IMngr, IMngrSnapshot, Mngr } from '../mngr/Mngr';
import { IMonitor, IMonitorSnapshot, Monitor } from '../monitor/Monitor';
import { IServiceHist, IServiceHistSnapshot, ServiceHist } from '../service-hist/ServiceHist';

/**
 * # ServiceHistStore
 *
 * ServiceHistStore을 설명하세요.
 */
export const ServiceHistStore = types
  .model('ServiceHistStore')
  // --------------------------------------------------------------------------
  .props({
    serviceHist: types.optional(types.array(ServiceHist), []),
    monitor: types.optional(types.array(Monitor), []),
    incident: types.optional(types.array(Incident), []),
    mngr: types.optional(types.array(Mngr), []),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setServiceHist: (serviceHist: IServiceHist[]) => {
      self.serviceHist.replace(serviceHist);
    },
    setMonitor: (monitor: IMonitor[]) => {
      self.monitor.replace(monitor);
    },
    setIncident: (incident: IIncident[]) => {
      self.incident.replace(incident);
    },
    setMngr: (mngr: IMngr[]) => {
      self.mngr.replace(mngr);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    getSections: async (types: Array<'serviceHist' | 'monitor' | 'incident' | 'mngr'>) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const fetchMap = {
        serviceHist: () => serviceHistApi.getServiceHist({ useYn: 'Y' }),
        monitor: () => serviceHistApi.getMonitor({ useYn: 'Y' }),
        incident: () => serviceHistApi.getIncident({ useYn: 'Y' }),
        mngr: () => serviceHistApi.getMngr({ useYn: 'Y' }),
      };
      const setterMap = {
        serviceHist: self.setServiceHist,
        monitor: self.setMonitor,
        incident: self.setIncident,
        mngr: self.setMngr,
      };
      try {
        const promises = types.map(async (type) => {
          const result = await fetchMap[type]();
          self.rootStore.responseStore.getResponseResult(result);

          if (result.kind === 'ok') {
            if (type === 'serviceHist' && 'serviceHist' in result) {
              setterMap[type](result.serviceHist);
            } else if (type === 'monitor' && 'monitor' in result) {
              setterMap[type](result.monitor);
            } else if (type === 'incident' && 'incident' in result) {
              setterMap[type](result.incident);
            } else if (type === 'mngr' && 'mngr' in result) {
              setterMap[type](result.mngr);
            }
          } else {
            console.error(`get${type} 실패`, result.kind);
          }
        });

        await Promise.all(promises);
      } catch (e) {
        console.error('getSections error:', e);
      }
    },

    getServiceHist: async () => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.getServiceHist();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setServiceHist(result.serviceHist);
      } else {
        console.error(result.kind);
      }
    },
    saveServiceHist: async (serviceHist: IServiceHistSnapshot) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.saveServiceHist(serviceHist);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setServiceHist([
          ...self.serviceHist.filter(
            (item) => item.serviceHistSid !== result.serviceHist.serviceHistSid,
          ),
          result.serviceHist,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteServiceHist: async (serviceHistSid: number) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result: TDeleteServiceHistResult = await serviceHistApi.deleteIncident(serviceHistSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getMonitor: async () => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.getMonitor();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setMonitor(result.monitor);
      } else {
        console.error(result.kind);
      }
    },
    saveMonitor: async (monitor: IMonitorSnapshot) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.saveMonitor(monitor);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setMonitor([
          ...self.monitor.filter((item) => item.monitorSid !== result.monitor.monitorSid),
          result.monitor,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteMonitor: async (monitorSid: number) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result: TDeleteServiceHistResult = await serviceHistApi.deleteMonitor(monitorSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getIncident: async () => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.getIncident();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setIncident(result.incident);
      } else {
        console.error(result.kind);
      }
    },
    saveIncident: async (incident: IIncidentSnapshot) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.saveIncident(incident);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setIncident([
          ...self.incident.filter((item) => item.incidentSid !== result.incident.incidentSid),
          result.incident,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteIncident: async (incidentSid: number) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result: TDeleteServiceHistResult = await serviceHistApi.deleteIncident(incidentSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getMngr: async () => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.getMngr();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setMngr(result.mngr);
      } else {
        console.error(result.kind);
      }
    },
    saveMngr: async (mngr: IMngrSnapshot) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result = await serviceHistApi.saveMngr(mngr);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setMngr([
          ...self.mngr.filter((item) => item.mngrSid !== result.mngr.mngrSid),
          result.mngr,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteMngr: async (mngrSid: number) => {
      const serviceHistApi: ServiceHistApi = new ServiceHistApi(self.environment.api);
      const result: TDeleteServiceHistResult = await serviceHistApi.deleteMngr(mngrSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TServiceHistStore = Instance<typeof ServiceHistStore>;
type TServiceHistStoreSnapshot = SnapshotOut<typeof ServiceHistStore>;

export interface IServiceHistStore extends TServiceHistStore {}
export type TServiceHistStoreKeys = keyof TServiceHistStoreSnapshot & string;
export interface IServiceHistStoreSnapshot extends TServiceHistStoreSnapshot {}
export const createServiceHistStore = () =>
  types.optional(ServiceHistStore, {} as TServiceHistStore);
